/**
 * Public waiting room for squad invites of any competition.
 * URL: /invite/<competitionId>/<inviteCode>
 *
 * The World Cup keeps its own /wc-invite/<code> route (links already in the wild);
 * every other competition carries its id in the link, because invite codes are
 * only unique per competition.
 */

import { useMemo } from 'react';
import { useLocalSearchParams, Redirect } from 'expo-router';
import { ErrorBoundary } from '../../../components/layout';
import { WorldCupInviteWaitingRoom } from '../../../components/onboarding/WorldCupInviteWaitingRoom/WorldCupInviteWaitingRoom';

const INVITE_CODE_RE = /^[A-Za-z0-9-]{4,32}$/;
const COMPETITION_ID_RE = /^[A-Za-z0-9-]{3,64}$/;

function firstParam(value?: string | string[]): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

export default function CompetitionInviteRoute() {
  const { competitionId, code } = useLocalSearchParams<{
    competitionId?: string | string[];
    code?: string | string[];
  }>();

  const parsed = useMemo(() => {
    const id = firstParam(competitionId);
    const inviteCode = firstParam(code);
    if (!id || !inviteCode) return null;
    if (!COMPETITION_ID_RE.test(id) || !INVITE_CODE_RE.test(inviteCode)) return null;
    return { id, inviteCode };
  }, [competitionId, code]);

  if (!parsed) {
    return <Redirect href="/" />;
  }

  return (
    <ErrorBoundary>
      <WorldCupInviteWaitingRoom competitionId={parsed.id} inviteCode={parsed.inviteCode} />
    </ErrorBoundary>
  );
}
